import React, { useState } from 'react';
import { supabase } from '@/core/database/supabase';
import type { WorkOrderDetailViewModel } from '../types/workOrderDetail.types';
import { toast } from 'sonner';

interface ProgressUpdateModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: WorkOrderDetailViewModel;
  onSuccess: () => void;
}

export function ProgressUpdateModal({ isOpen, onClose, data, onSuccess }: ProgressUpdateModalProps) {
  const [progress, setProgress] = useState(data.progress);
  const [note, setNote] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      
      const { data: { user } } = await supabase.auth.getUser();
      
      // 1. Update work_orders table first (Most likely to fail due to missing column)
      const { error: updateError } = await supabase
        .from('work_orders')
        .update({ 
          progress_percent: progress,
          status_code: progress === 100 ? 'completed' : 'in_progress',
          started_at: data.actualStart || new Date().toISOString(),
          completed_at: progress === 100 ? new Date().toISOString() : null
        })
        .eq('id', data.id);

      if (updateError) {
        // If progress_percent is missing, try updating without it to at least save dates/status
        if (updateError.message.includes('progress_percent')) {
          console.warn('[ProgressUpdateModal] progress_percent column missing, skipping field update');
          const { error: retryError } = await supabase
            .from('work_orders')
            .update({ 
              status_code: progress === 100 ? 'completed' : 'in_progress',
              started_at: data.actualStart || new Date().toISOString(),
              completed_at: progress === 100 ? new Date().toISOString() : null
            })
            .eq('id', data.id);
          if (retryError) throw retryError;
        } else {
          throw updateError;
        }
      }

      // 2. Insert into progress_logs only if update succeeded
      const { error: logError } = await supabase
        .from('progress_logs')
        .insert([{
          work_order_id: data.id,
          project_id: data.projectId,
          company_id: data.companyId,
          progress_percent: progress,
          summary: note,
          log_date: new Date().toISOString().split('T')[0],
          status_code: progress === 100 ? 'approved' : 'submitted',
          created_by: user?.id
        }]);

      if (logError) throw logError;

      // 3. Record Audit Log
      await supabase
        .from('audit_logs')
        .insert([{
          project_id: data.projectId,
          company_id: data.companyId,
          actor_user_id: user?.id,
          action_code: 'update',
          module_code: 'work_orders',
          entity_type: 'work_orders',
          entity_id: data.id,
          summary: `Cập nhật tiến độ lệnh: ${data.code} lên ${progress}%`,
          before_data: { progress_percent: data.progress },
          after_data: { progress_percent: progress, note: note }
        }]);

      toast.success('Cập nhật tiến độ thành công');
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('[ProgressUpdateModal] Error:', error);
      toast.error('Có lỗi xảy ra: ' + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-md w-full max-w-md overflow-hidden">
        <div className="p-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="sm:text-sm text-xs text-slate-900">Cập nhật tiến độ</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        <div className="p-4 space-y-6">
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <label className="sm:text-sm text-xs text-slate-500">Tiến độ hiện tại (%)</label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={progress}
                  onChange={(e) => {
                    const val = Math.min(100, Math.max(0, Number(e.target.value)));
                    setProgress(val);
                  }}
                  className="w-16 h-8 p-1 rounded-md border border-slate-200 text-center sm:text-sm text-xs text-emerald-600 font-normal focus:outline-none focus:ring-1 focus:ring-emerald-500"
                />
              </div>
            </div>
            <input
              type="range"
              min="0"
              max="100"
              value={progress}
              onChange={(e) => setProgress(Number(e.target.value))}
              className="w-full h-2 bg-slate-100 rounded-lg appearance-none cursor-pointer accent-emerald-600"
            />
            <div className="flex justify-between sm:text-sm text-xs text-slate-400">
              <span>0%</span>
              <span>50%</span>
              <span>100%</span>
            </div>
          </div>

          <div className="space-y-2">
            <label className="sm:text-sm text-xs text-slate-500">Ghi chú tiến độ</label>
            <textarea
              placeholder="Mô tả ngắn gọn nội dung đã hoàn thành..."
              className="w-full h-24 p-3 rounded-md border border-slate-200 sm:text-sm text-xs focus:outline-none focus:ring-1 focus:ring-emerald-500 bg-slate-50/50 resize-none"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>
        </div>

        <div className="p-4 bg-slate-50 flex gap-2 justify-end">
          <button
            onClick={onClose}
            className="h-9 px-4 rounded-md border border-slate-200 bg-white sm:text-sm text-xs text-slate-600 hover:bg-slate-50"
            disabled={isSubmitting}
          >
            Hủy
          </button>
          <button
            onClick={handleSubmit}
            className="h-9 px-4 rounded-md bg-emerald-700 sm:text-sm text-xs text-white hover:bg-emerald-800 flex items-center gap-2"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <i className="ri-loader-4-line animate-spin" />
            ) : (
              <i className="ri-check-line" />
            )}
            Lưu cập nhật
          </button>
        </div>
      </div>
    </div>
  );
}
